/* Cart offer engine — assets/section-cart-offers.js
   Loaded by sections/cart-drawer.liquid (and by sections/cart.liquid for the
   cart-page redirect only).

   Listens to the theme's own cart events (theme:cart:change / theme:cart:reload)
   and layers offer behaviour on top of the untouched native cart:
   - auto-add / auto-remove tiered gifts (site_gwp → cart_gift_rule)
   - claimed gifts persistence (cart_claim_rule + localStorage)
   - per-product freebie pairing (cart_freebie)
   - BOGO auto-add with reverse offers (cart_bogo_offer)
   - max-per-item quantity cap
   - drafted / out-of-stock gift handling with timed retries
   - cart page kill: /cart redirects home and opens the drawer
   - anti-tamper guard on console-initiated cart calls

   Config comes from the JSON block rendered by snippets/cart-offers-config-json.liquid.

   Loop safety: the engine only POSTs /cart/update.js when the desired gift lines
   differ from the cart's actual lines, so the theme:cart:change it dispatches
   afterwards produces an empty diff and the cycle terminates.

   Race safety: single-flight — one update request at a time; changes arriving
   mid-flight set a dirty flag and exactly one re-evaluation runs afterwards. */
(function () {
  'use strict';

  /* ── Anti-tamper guard ────────────────────────────────────────────────────
     Blocks cart mutations initiated from the browser console. The engine and
     the rest of the theme keep working through the captured original fetch. */
  var originalFetch = window.fetch.bind(window);

  window.fetch = function () {
    var url = arguments[0];
    var isCartEndpoint =
      typeof url === 'string' &&
      ['/cart/add.js', '/cart/update.js', '/cart/change.js'].some(function (path) {
        return url.indexOf(path) !== -1;
      });

    if (isCartEndpoint) {
      var stack = new Error().stack || '';
      if (stack.indexOf('at <anonymous>') !== -1 || stack.indexOf('VM') !== -1) {
        return Promise.reject('Blocked');
      }
    }

    return originalFetch.apply(window, arguments);
  };

  /* ── Routes ─────────────────────────────────────────────────────────────── */

  function rootUrl() {
    return ((window.theme && window.theme.routes && window.theme.routes.root_url) || '/').replace(/\/?$/, '/');
  }

  function cartRoute() {
    return (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  }

  /* ── Cart page kill ───────────────────────────────────────────────────────
     /cart is not a surface: send the visitor home and open the drawer there. */
  if (window.location.pathname === cartRoute()) {
    var redirect = new URL(rootUrl(), window.location.origin);
    redirect.searchParams.set('open_cart', '1');
    window.location.replace(redirect.toString());
    return;
  }

  /* ── Config + state ─────────────────────────────────────────────────────── */

  var CLAIM_STORAGE_KEY = 'claimedGiftProducts';
  var BUNDLE_MEMORY_KEY = 'asayaBundleGifts';
  var UNAVAILABLE_RETRY_INTERVAL = 60000;
  var RECALC_DEBOUNCE = 100;

  var config = null;
  var cart = null;
  var inFlight = false;
  var dirty = false;
  var recalcTimer = null;
  var recalcBusyAttempts = 0;
  var unavailableGiftVariants = [];
  var unavailableGiftLastAttempt = 0;
  var bogoOfferDrafted = false;
  var gokwikReady = false;
  var checkoutLocked = false;
  var engineWantsChange = false;
  var pendingGiftRemovals = [];
  var nativeBusy = false;
  var nativeBusyTimer = null;
  var addBusy = false;
  var bundleRemovals = {};
  var bundleRestorable = false;

  /* A drawer add (BOGO/bundle/upsell) is committing to the server. The cart on
     screen is about to change, so lock checkout until the add settles. */
  function setAddBusy(state) {
    addBusy = state;
    updateCheckoutLock();
  }

  function readConfig() {
    var el = document.querySelector('[data-cart-offers-config]');
    if (!el) return null;
    try {
      var parsed = JSON.parse(el.textContent);
      parsed.priceMarkers = String(parsed.priceMarkers || '0:100')
        .split(':')
        .map(function (marker) { return parseInt(marker, 10); })
        .filter(function (marker) { return !isNaN(marker); });
      parsed.maxItemQuantity = parseInt(parsed.maxItemQuantity, 10) || 40;
      return parsed;
    } catch (e) {
      console.error('[cart-offers] invalid config JSON', e);
      return null;
    }
  }

  /* Null-safe string lookup — UI handlers stay clickable even when the config
     block is missing or failed to parse. */
  function str(key) {
    return (config && config.strings && config.strings[key]) || '';
  }

  function getClaimedGifts() {
    try {
      return JSON.parse(localStorage.getItem(CLAIM_STORAGE_KEY)) || null;
    } catch (e) {
      return null;
    }
  }

  function setClaimedGifts(store) {
    try {
      localStorage.setItem(CLAIM_STORAGE_KEY, JSON.stringify(store));
    } catch (e) { /* storage unavailable — claim still applies this session */ }
  }

  function clearClaimedGifts() {
    try {
      localStorage.removeItem(CLAIM_STORAGE_KEY);
    } catch (e) { /* storage unavailable */ }
  }

  /* Claim store values are { q, t }: q = quantity, t = add-time (ms), used to
     drop the most-recently-claimed first when over a max_claimable cap (LIFO).
     Back-compat: older entries were a bare number (the quantity, no time). */
  function claimQtyOf(val) {
    if (val == null) return 0;
    return typeof val === 'number' ? val : (val.q || 0);
  }
  function claimTimeOf(val) {
    return (val && typeof val === 'object' && typeof val.t === 'number') ? val.t : 0;
  }

  /* Bundle freebie memory — sessionStorage (deliberately NOT localStorage like
     claim gifts): "the shopper deleted their free gift" should reset on a new
     session, not follow them for weeks.
     Shape: { added: {variantId: 1}, removed: {variantId: 1} }
     - added: freebies this engine auto-added and has seen in the cart, so a
       later disappearance while the tier still holds reads as a manual removal
     - removed: freebies the shopper manually removed — never re-added this
       session until the restore link clears the flag */
  function getBundleMemory() {
    try {
      var stored = JSON.parse(sessionStorage.getItem(BUNDLE_MEMORY_KEY)) || {};
      return { added: stored.added || {}, removed: stored.removed || {} };
    } catch (e) {
      return { added: {}, removed: {} };
    }
  }

  function setBundleMemory(memory) {
    try {
      sessionStorage.setItem(BUNDLE_MEMORY_KEY, JSON.stringify(memory));
    } catch (e) { /* storage unavailable — auto-add just repeats next page load */ }
  }

  /* ── Cart math ──────────────────────────────────────────────────────────── */

  function isGiftLine(item) {
  return (
    (
      config.priceMarkers.indexOf(item.final_line_price) !== -1 &&
      config.priceMarkers.indexOf(item.original_line_price) !== -1
    ) ||
    (
      item.final_line_price === 0 &&
      item.original_line_price === 0
    ) ||
    (
      item.final_line_price === 100 &&
      item.original_line_price === 100
    )
  );
}

  function isExcludedType(item) {
    return (config.excludedTypes || []).some(function (type) {
      return type && item.product_type === type;
    });
  }

  /* Paid totals exclude gift lines always; excluded product types are summed
     separately so per-rule include_excluded_types overrides can add them back.
     Prices use original_line_price (pre-discount) so gift/claim/BOGO thresholds
     are evaluated against the subtotal the shopper actually reached — a coupon
     that lowers final_line_price must NOT retroactively revoke a gift already
     earned. This mirrors the Liquid progress bar, which gates its stages on the
     same pre-discount original_line_price (see snippets/cart-offers.liquid). */
  function computeTotals() {
    var totals = { paidQty: 0, paidPrice: 0, excludedQty: 0, excludedPrice: 0 };
    (cart.items || []).forEach(function (item) {
      if (isGiftLine(item)) return;
      if (isExcludedType(item)) {
        totals.excludedQty += item.quantity;
        totals.excludedPrice += item.original_line_price;
        return;
      }
      totals.paidQty += item.quantity;
      totals.paidPrice += item.original_line_price;
    });
    return totals;
  }

  function ruleQty(rule, totals) {
    return rule.includeExcludedTypes ? totals.paidQty + totals.excludedQty : totals.paidQty;
  }

  function rulePrice(rule, totals) {
    return rule.includeExcludedTypes ? totals.paidPrice + totals.excludedPrice : totals.paidPrice;
  }

  /* Pick the threshold a rule is evaluated against, honouring the global
     is_price_based mode. The mode's field is preferred; if the admin left it
     blank, fall back to the other field so a rule with only one threshold still
     works. Returns null when the rule has no usable threshold at all.
     triggerPrice is in major units (rupees) — cart prices are in the smallest
     unit, so it's compared against triggerPrice * 100. */
  function ruleThreshold(rule, totals) {
    var hasPrice = rule.triggerPrice !== null && rule.triggerPrice !== undefined;
    var hasQty = rule.triggerQuantity !== null && rule.triggerQuantity !== undefined;
    var usePrice;
    if (config.isPriceBased) {
      usePrice = hasPrice ? true : (hasQty ? false : null);
    } else {
      usePrice = hasQty ? false : (hasPrice ? true : null);
    }
    if (usePrice === null) return null;
    if (usePrice) {
      return { value: rulePrice(rule, totals), target: rule.triggerPrice * 100 };
    }
    return { value: ruleQty(rule, totals), target: rule.triggerQuantity };
  }

  /* Highest applicable rule wins (compared on its own target). */
  function applicableRule(rules, totals) {
    var applicable = [];
    (rules || []).forEach(function (rule) {
      var t = ruleThreshold(rule, totals);
      if (t && t.value >= t.target) applicable.push({ rule: rule, target: t.target });
    });

    applicable.sort(function (a, b) { return b.target - a.target; });

    return applicable.length ? applicable[0].rule : null;
  }

  function variantQtyInCart(variantId) {
    return (cart.items || []).reduce(function (sum, item) {
      return String(item.id) === String(variantId) ? sum + item.quantity : sum;
    }, 0);
  }

  /* A line belongs to a BOGO trigger collection when its product id is in the
     (capped) config list, or its rendered row carries the collection id. */
  function lineInCollection(item, offer) {
    if ((offer.triggerProductIds || []).some(function (pid) { return String(pid) === String(item.product_id); })) {
      return true;
    }
    var row = document.querySelector('[data-cart-item][data-variant-id="' + item.id + '"]');
    if (row) {
      try {
        var ids = JSON.parse(row.getAttribute('data-collection-ids') || '[]');
        return ids.some(function (cid) { return String(cid) === String(offer.triggerCollectionId); });
      } catch (e) { /* fall through */ }
    }
    return false;
  }

  /* A bundle offer applies to this cart when it's enabled (active + date
     window, evaluated in Liquid) and — for testMode offers — the cart carries
     the _bundle_test=1 attribute. Test mode lets the live store trial an offer
     invisibly: the pricing function applies the exact same gate server-side. */
  function bundleOfferUsable(offer) {
    if (!config || !config.saleModeEnabled) return false; // master switch (shop metafield)
    if (!offer.enabled) return false;
    if (!offer.testMode) return true;
    return !!(cart && cart.attributes && cart.attributes._bundle_test === '1');
  }

  /* Sale cart mode: when the shop metafield is on AND a bundle offer is usable
     for this cart session, the bundle system replaces the GWP system — gift
     rules, claims, freebie pairings and BOGO all pause (mirrors the Liquid
     branch in cart-offers.liquid via cart-bundle-mode). */
  function saleCartActive() {
    return (config.bundleOffers || []).some(bundleOfferUsable);
  }

  /* Variants flagged custom.bundle_excluded (multipacks/combos) sit out of
     bundle offers entirely. The flag reaches the engine via the rendered row's
     data-bundle-excluded attribute (cart.js has no metafields); a not-yet
     rendered row briefly reads as not-excluded — the pricing function is
     authoritative and the next re-render self-corrects. */
  function lineBundleExcluded(item) {
    var row = document.querySelector('[data-cart-item][data-variant-id="' + item.id + '"]');
    return !!(row && row.getAttribute('data-bundle-excluded') === 'true');
  }

  /* Unit pool for a bundle offer's tier simulation. Mirrors the pricing
     function in the asaya-offers app: gift lines, freebie variants and
     bundle-excluded variants never count; collection scope uses the same
     capped-list + data-collection-ids check as BOGO. Restricted products keep
     ALL their units here, grouped per product — the max-1 rule is applied PER
     BUNDLE INSTANCE by the grant loop (same as the function's pickUnits), so
     leftovers of different restricted products can seat a later stacked
     instance. Unit prices (original, pre-discount — the catalog price the
     function sees) drive the same cheapest-first consumption order. */
  function bundleUnitPool(offer, freebieVids) {
    var pool = { unrestricted: [], restrictedUnits: {} };
    (cart.items || []).forEach(function (item) {
      if (isGiftLine(item)) return;
      if (freebieVids[String(item.id)]) return;
      if (lineBundleExcluded(item)) return;
      if (offer.scope === 'collection' && !lineInCollection(item, offer)) return;
      if (offer.scope === 'products' && !(offer.eligibleVariantIds || []).some(function (vid) {
        return String(vid) === String(item.id);
      })) return;
      var restricted = (offer.restrictedProductIds || []).some(function (pid) {
        return String(pid) === String(item.product_id);
      });
      for (var u = 0; u < item.quantity; u++) {
        if (restricted) {
          (pool.restrictedUnits[item.product_id] = pool.restrictedUnits[item.product_id] || [])
            .push(item.original_price);
        } else {
          pool.unrestricted.push(item.original_price);
        }
      }
    });
    pool.unrestricted.sort(function (a, b) { return a - b; });
    Object.keys(pool.restrictedUnits).forEach(function (pid) {
      pool.restrictedUnits[pid].sort(function (a, b) { return a - b; });
    });
    return pool;
  }

  /* ── Desired auto-managed lines ─────────────────────────────────────────── */

  function desireGift(desired, variantId, applicableAutoVariants, now) {
    var vid = String(variantId);
    applicableAutoVariants.push(vid);

    if ((config.oosGiftVariants || []).some(function (id) { return String(id) === vid; })) {
      if (unavailableGiftVariants.indexOf(vid) === -1) unavailableGiftVariants.push(vid);
      return;
    }

    if (unavailableGiftVariants.indexOf(vid) === -1) {
      desired[vid] = 1;
    } else if (now - unavailableGiftLastAttempt >= UNAVAILABLE_RETRY_INTERVAL) {
      desired[vid] = 1;
      unavailableGiftVariants = unavailableGiftVariants.filter(function (id) { return id !== vid; });
      unavailableGiftLastAttempt = now;
    }
  }

  function computeDesired() {
    var totals = computeTotals();
    var desired = {};
    var applicableAutoVariants = [];
    var now = Date.now();
    var saleActive = saleCartActive();
    bogoOfferDrafted = false;

    /* Tiered gifts (paused in sale cart mode) */
    var giftRule = saleActive ? null : applicableRule(config.giftRules, totals);
    if (giftRule) {
      (giftRule.gifts || []).forEach(function (vid) {
        desireGift(desired, vid, applicableAutoVariants, now);
      });
    }

    /* Claimed gifts — shopper's choice persists while a claim rule applies
       (paused in sale mode WITHOUT clearing claims: they resume after the sale) */
    var claimRule = saleActive ? null : applicableRule(config.claimRules, totals);
    if (claimRule) {
      var claimed = getClaimedGifts();
      if (claimed) {
        /* Honour max_claimable (null/undefined = unlimited). Gather this rule's
           active claims with their add-time, then apply the EARLIEST up to the
           cap — so when over the cap (e.g. admin lowered it) the most-recently
           claimed gifts are the ones dropped (LIFO). */
        var cap = (claimRule.maxClaimable === null || claimRule.maxClaimable === undefined)
          ? Infinity : claimRule.maxClaimable;
        var ruleClaims = [];
        (claimRule.gifts || []).forEach(function (vid) {
          var key = String(vid);
          var q = claimQtyOf(claimed[key]);
          if (q > 0) ruleClaims.push({ key: key, q: q, t: claimTimeOf(claimed[key]) });
        });
        ruleClaims.sort(function (a, b) { return a.t - b.t; });
        ruleClaims.slice(0, cap).forEach(function (c) {
          desired[c.key] = c.q;
          applicableAutoVariants.push(c.key);
        });
      }
    } else if (!saleActive) {
      clearClaimedGifts();
    }

    /* Per-product freebie pairing (paused in sale cart mode) */
    (saleActive ? [] : config.freebies || []).forEach(function (pairing) {
      if (!pairing.freebie || !pairing.freebie.length) return;
      var triggered = (pairing.products || []).some(function (vid) {
        return variantQtyInCart(vid) > 0 && !Object.prototype.hasOwnProperty.call(desired, String(vid));
      });
      if (triggered) {
        desireGift(desired, pairing.freebie[0], applicableAutoVariants, now);
      }
    });
    /********************************** custom product add ******************************************* */
    /* Tinted Sunscreen → Pearl Charm */

// var productGiftMap = {
//   // Main Product ID : Free Gift Variant ID
//   "9648174924089": "54422073246009"
// };

// (cart.items || []).forEach(function (item) {
//   var giftVariantId = productGiftMap[String(item.product_id)];

//   if (giftVariantId) {
//     desireGift(
//       desired,
//       giftVariantId,
//       applicableAutoVariants,
//       now
//     );
//   }
// });

// var productGiftMap = {
//   // Main Product ID : Free Gift Variant ID
//   "9648174924089": "54422073246009"
// };

// // Run this gift logic only for India
// var isIndiaStore =
//   window.Shopify &&
//   window.Shopify.country === "IN";

// if (isIndiaStore) {
//   (cart.items || []).forEach(function (item) {
//     var giftVariantId = productGiftMap[String(item.product_id)];

//     if (giftVariantId) {
//       desireGift(
//         desired,
//         giftVariantId,
//         applicableAutoVariants,
//         now
//       );
//     }
//   });
// }


// /********************************** custom product add end******************************************* */
    /* BOGO offers (paused in sale cart mode) */
    (saleActive ? [] : config.bogoOffers || []).forEach(function (offer) {
      var triggerMet = false;
      var reverseMet = false;

      if (offer.isPriceBase) {
        var triggerPriceNeeded = Number(offer.triggerPrice || 0) * 100;
        var triggerTotal = (cart.items || []).reduce(function (sum, item) {
          if (isGiftLine(item)) return sum;
          if (isExcludedType(item) && !offer.includeExcludedTypes) return sum;
          /* Pre-discount, same reasoning as computeTotals: a coupon must not
             pull the collection subtotal back below the BOGO trigger. */
          return lineInCollection(item, offer) ? sum + item.original_line_price : sum;
        }, 0);
        var reverseTotal = rulePrice(offer, totals);
        if (triggerTotal > reverseTotal) triggerTotal = reverseTotal;
        triggerMet = triggerTotal >= triggerPriceNeeded;
        reverseMet = reverseTotal >= triggerPriceNeeded;
      } else {
        var triggerCountNeeded = Number(offer.triggerCount || 1);
        var triggerQty = (cart.items || []).reduce(function (sum, item) {
          if (isGiftLine(item)) return sum;
          if (isExcludedType(item) && !offer.includeExcludedTypes) return sum;
          return lineInCollection(item, offer) ? sum + item.quantity : sum;
        }, 0);
        triggerMet = triggerQty >= triggerCountNeeded;
      }

      if (offer.autoAdd) {
        /* Auto-add needs a single offer variant. */
        if ((offer.offerVariants || []).length !== 1 && !offer.offerUnavailable && !offer.reverseOfferUnavailable) return;
        var offerVid = (offer.offerVariants || [])[0];
        var reverseVid = (offer.reverseOfferVariants || [])[0];
        if (triggerMet) {
          if (offer.offerUnavailable) bogoOfferDrafted = true;
          else if (offerVid) desireGift(desired, offerVid, applicableAutoVariants, now);
        } else if (reverseMet) {
          if (offer.reverseOfferUnavailable) bogoOfferDrafted = true;
          else if (reverseVid) desireGift(desired, reverseVid, applicableAutoVariants, now);
        }
      } else {
        /* Manual: the shopper adds the reward from the card. Keep whatever offer
           (or reverse) variants they've added while the trigger holds, so the
           gift-line cleanup can't strip a manually-added free reward; they drop
           off when the trigger no longer applies. */
        var keepList = triggerMet ? offer.offerVariants : (reverseMet ? offer.reverseOfferVariants : []);
        (keepList || []).forEach(function (vid) {
          if (variantQtyInCart(vid) > 0) desireGift(desired, vid, applicableAutoVariants, now);
        });
      }
    });

    /* Tiered bundle offers (bundle_offer metaobjects). The asaya-offers app's
       discount function does the pricing server-side; the theme only manages the
       freebie line's PRESENCE: desire it when its tier is reached, drop it when
       the tier is lost.

       Bundle freebies are not marker-priced gifts — their original price is
       real, only the function-discounted price is 0 — so the generic gift
       cleanup in computeUpdates can't remove them. bundleRemovals marks
       engine-added freebies whose tier was lost so the same single diff cycle
       takes them out (otherwise the function stops discounting and the shopper
       silently pays full price for the "gift"). */
    bundleRemovals = {};
    bundleRestorable = false;
    var bundleMemory = getBundleMemory();
    var bundleMemoryChanged = false;
    var allBundleFreebies = {};
    (config.bundleOffers || []).forEach(function (offer) {
      if (!bundleOfferUsable(offer)) return;
      (offer.tiers || []).forEach(function (tier) {
        (tier.freebieVariantIds || []).forEach(function (vid) { allBundleFreebies[String(vid)] = true; });
      });
    });

    /* Sale mode switched off (or an offer deactivated / test window closed)
       while an engine-added freebie is still in the cart: the offer skips the
       usable-gated loop below, so the tier-lost cleanup never sees it — and
       the function no longer prices it to 0. Give unusable offers' freebies
       the same removal treatment here. */
    (config.bundleOffers || []).forEach(function (offer) {
      if (bundleOfferUsable(offer)) return;
      (offer.tiers || []).forEach(function (tier) {
        (tier.freebieVariantIds || []).concat(tier.valueGiftVariantIds || []).forEach(function (vid) {
          vid = String(vid);
          if (!bundleMemory.added[vid]) return;
          if (variantQtyInCart(vid) > 0) bundleRemovals[vid] = true;
          delete bundleMemory.added[vid];
          bundleMemoryChanged = true;
        });
      });
    });

    (config.bundleOffers || []).forEach(function (offer) {
      if (!bundleOfferUsable(offer)) return;
      var pool = bundleUnitPool(offer, allBundleFreebies);
      /* Granted tiers, mirroring the pricing function: greedy largest-first,
         repeating (smaller tiers fill the remainder) when stackTiers is on,
         once when off. Each instance seats at most 1 unit per restricted
         product and consumes its units cheapest-first, exactly like the
         function's pickUnits — so the tiers this loop grants (and therefore
         the freebies it desires) match what the function will price.
         Freebies stay capped at 1 per variant either way. */
      var wantedVids = [];
      var unitValueCovered = {};
      /* restrictedRebundle OFF (default): a restricted product is one-shot for
         the whole cart — once an instance seats it, later instances can't.
         ON: only the per-instance cap applies (leftovers can re-bundle). */
      var cartUsedRestricted = {};
      var tiersDesc = (offer.tiers || []).slice().sort(function (a, b) { return b.quantity - a.quantity; });
      for (;;) {
        var restrictedAvail = 0;
        Object.keys(pool.restrictedUnits).forEach(function (pid) {
          if (pool.restrictedUnits[pid].length > 0 && (offer.restrictedRebundle || !cartUsedRestricted[pid])) {
            restrictedAvail++;
          }
        });
        var avail = pool.unrestricted.length + restrictedAvail;

        var granted = null;
        for (var ti = 0; ti < tiersDesc.length; ti++) {
          if (tiersDesc[ti].quantity > 0 && tiersDesc[ti].quantity <= avail) {
            granted = tiersDesc[ti];
            break;
          }
        }
        if (!granted) break;

        /* Consume the instance's units: candidates are every unrestricted unit
           plus the cheapest remaining unit of each seatable restricted product. */
        var cands = pool.unrestricted.map(function (price) { return { price: price, pid: null }; });
        Object.keys(pool.restrictedUnits).forEach(function (pid) {
          if (pool.restrictedUnits[pid].length > 0 && (offer.restrictedRebundle || !cartUsedRestricted[pid])) {
            cands.push({ price: pool.restrictedUnits[pid][0], pid: pid });
          }
        });
        cands.sort(function (a, b) { return a.price - b.price; });
        var takeUnrestricted = 0;
        cands.slice(0, granted.quantity).forEach(function (cand) {
          if (cand.pid !== null) {
            pool.restrictedUnits[cand.pid].shift();
            cartUsedRestricted[cand.pid] = true;
          } else {
            takeUnrestricted++;
          }
        });
        pool.unrestricted.splice(0, takeUnrestricted);

        (granted.freebieVariantIds || []).forEach(function (vid) {
          if (wantedVids.indexOf(String(vid)) === -1) wantedVids.push(String(vid));
        });
        (granted.valueGiftVariantIds || []).forEach(function (vid) {
          unitValueCovered[String(vid)] = true;
        });
        if (!offer.stackTiers) break;
      }

      /* Value ladder: tier PRICES double as cart-value thresholds — the
         post-discount subtotal (what the shopper sees) is compared against
         each tier's price. With the offer's quantityGifting toggle on, tier
         QUANTITIES unlock the same gifts too: any non-gift item counts (whole
         cart, full quantity, eligibility ignored), and a tier is reached by
         EITHER its price or its quantity. The highest tier reached grants its
         value gifts — ₹0 marker variants, NOT function-priced lines, so
         FLAT-10/FLAT-20 (which don't combine with the function) can never
         charge for them. A tier granted by units marks its own value gifts as
         covered above, so the unit and value paths can never double-gift the
         same variant. */
      var cartValue = 0;
      var cartQty = 0;
      (cart.items || []).forEach(function (item) {
        cartValue += item.final_line_price;
        if (!isGiftLine(item)) cartQty += item.quantity;
      });
      var valueTier = null;
      (offer.tiers || []).forEach(function (tier) {
        if (!(tier.valueGiftVariantIds || []).length) return;
        var byValue = tier.price && cartValue >= tier.price * 100;
        var byQty = offer.quantityGifting && tier.quantity > 0 && cartQty >= tier.quantity;
        if (!byValue && !byQty) return;
        if (!valueTier || tier.quantity > valueTier.quantity) valueTier = tier;
      });
      if (valueTier) {
        (valueTier.valueGiftVariantIds || []).forEach(function (vid) {
          vid = String(vid);
          if (unitValueCovered[vid]) return;
          if (wantedVids.indexOf(vid) === -1) wantedVids.push(vid);
        });
      }

      var offerVids = {};
      (offer.tiers || []).forEach(function (tier) {
        (tier.freebieVariantIds || []).forEach(function (vid) { offerVids[String(vid)] = true; });
        (tier.valueGiftVariantIds || []).forEach(function (vid) { offerVids[String(vid)] = true; });
      });

      Object.keys(offerVids).forEach(function (vid) {
        var wanted = wantedVids.indexOf(vid) !== -1;
        var inCart = variantQtyInCart(vid) > 0;

        if (!wanted) {
          if (inCart && bundleMemory.added[vid]) {
            bundleRemovals[vid] = true; // tier lost → no longer free → take it out
          }
          if (bundleMemory.added[vid]) {
            delete bundleMemory.added[vid];
            bundleMemoryChanged = true;
          }
          return;
        }

        if (bundleMemory.removed[vid]) {
          if (inCart) {
            /* Shopper brought it back themselves — forget the removal. */
            delete bundleMemory.removed[vid];
            bundleMemory.added[vid] = 1;
            bundleMemoryChanged = true;
            desireGift(desired, vid, applicableAutoVariants, now);
          } else {
            bundleRestorable = true; // tier is met — offer the restore link
          }
          return;
        }

        if (inCart && !bundleMemory.added[vid]) {
          bundleMemory.added[vid] = 1;
          bundleMemoryChanged = true;
        } else if (!inCart && bundleMemory.added[vid]) {
          /* Engine added it, the tier still holds, the line is gone → the
             shopper removed it manually. Respect that for this session. */
          delete bundleMemory.added[vid];
          bundleMemory.removed[vid] = 1;
          bundleMemoryChanged = true;
          bundleRestorable = true;
          return;
        }

        desireGift(desired, vid, applicableAutoVariants, now);
      });
    });
    if (bundleMemoryChanged) setBundleMemory(bundleMemory);

    /* Forget unavailable variants that no offer wants any more */
    unavailableGiftVariants = unavailableGiftVariants.filter(function (vid) {
      return applicableAutoVariants.indexOf(vid) !== -1;
    });

    return desired;
  }

  /* ── Diff: desired vs actual ────────────────────────────────────────────── */

  function computeUpdates(desired) {
    var updates = {};
    var maxExceeded = false;
    var seen = {};

    (cart.items || []).forEach(function (item) {
      var vid = String(item.id);
      if (seen[vid]) return;
      seen[vid] = true;

      var totalQty = variantQtyInCart(vid);

      if (Object.prototype.hasOwnProperty.call(desired, vid)) {
        if (totalQty < desired[vid]) {
          updates[vid] = desired[vid];
        } else if (totalQty > desired[vid] && isGiftLine(item)) {
          /* Marker-priced gifts are capped at the desired quantity; a PAID line
             of the same variant (shopper bought extra) is never downgraded —
             forcing it back would fight the shopper's own quantity buttons. */
          updates[vid] = desired[vid];
        }
        return;
      }

      if (isGiftLine(item)) {
        /* Gift-priced line no offer wants → remove */
        updates[vid] = 0;
        return;
      }

      if (bundleRemovals[vid]) {
        /* Engine-added bundle freebie whose tier was lost — its original price
           is real (only the function's discount made it ₹0), so the marker
           check above can't catch it. */
        updates[vid] = 0;
        return;
      }

      if (totalQty > config.maxItemQuantity) {
        updates[vid] = config.maxItemQuantity;
        maxExceeded = true;
      }
    });

    Object.keys(desired).forEach(function (vid) {
      if (!seen[vid] && desired[vid] > 0) {
        updates[vid] = desired[vid];
      }
    });

    return { updates: updates, maxExceeded: maxExceeded };
  }

  /* ── Engine cycle ───────────────────────────────────────────────────────── */

  function scheduleRecalc() {
    clearTimeout(recalcTimer);
    recalcTimer = setTimeout(recalc, RECALC_DEBOUNCE);
  }

  /* Shopify serializes cart mutations per cart — a request sent while another
     is in flight can come back 409 with a non-JSON body, which surfaces as the
     theme's generic "All available stock is in cart" fallback. CartItems marks
     its in-flight requests with cart--loading, so the engine yields to it. */
  function nativeCartBusy() {
    return !!document.querySelector('[data-cart-loading].cart--loading');
  }

  function whenNativeIdle(run) {
    var attempts = 0;
    (function poll() {
      if (!nativeCartBusy() || attempts >= 20) {
        run();
        return;
      }
      attempts++;
      setTimeout(poll, 150);
    })();
  }

  function recalc() {
    if (!config || !cart) return;
    if (inFlight) {
      dirty = true;
      return;
    }
    recalcBusyAttempts = 0;
    recalcTimer = null;

    var desired = computeDesired();
    var diff = computeUpdates(desired);
    var payload = {};
    var hasUpdates = Object.keys(diff.updates).length > 0;

    /* Reconcile the UI up front. These are all network-free, so they must NOT
       wait on the native cart's in-flight render — that's what made a removed
       gift linger: the gift would only disappear a full round-trip later, once
       our removal POST landed. A gift line the engine is dropping (update → 0)
       is hidden immediately and kept hidden across re-renders (see
       applyGiftHides), so it vanishes together with the product that triggered
       it. pendingGiftRemovals is recomputed every cycle, so a gift that becomes
       wanted again (trigger re-added) un-hides on the next recalc. */
    pendingGiftRemovals = Object.keys(diff.updates).filter(function (vid) {
      return diff.updates[vid] === 0;
    });
    applyGiftHides();
    updateBundleRestoreUI();

    /* The engine still has gift lines to add/remove — keep checkout locked until
       this write lands and a follow-up recalc confirms desired == actual. */
    engineWantsChange = hasUpdates;
    updateCheckoutLock();
    updateGiftUnavailableUI();

    if (diff.maxExceeded) {
      showToast(str('maxQuantity'));
    }

    if (hasUpdates) payload.updates = diff.updates;

    /* Keep the page-scoped offer context readable by Section Rendering API
       refreshes (they always render from "/"). */
    var currentAttr = (cart.attributes && cart.attributes._offer_config) || '';
    if (config.configHandle && currentAttr !== config.configHandle) {
      payload.attributes = { _offer_config: config.configHandle };
    }

    if (!Object.keys(payload).length) return;

    var giftVariantIds = Object.keys(diff.updates).filter(function (vid) {
      return diff.updates[vid] > 0 && Object.prototype.hasOwnProperty.call(desired, vid);
    });

    sendUpdateWhenIdle(payload, giftVariantIds, hasUpdates);
  }

  /* Only the cart-mutating POST has to serialize behind the native cart's own
     writes (a concurrent write returns a body-less 409 → the theme's generic
     "All available stock is in cart"). The desired state is already computed and
     shown above, so we defer just the request — not the recompute, which would
     double the unavailable-gift retry bookkeeping. Capped at ~3s so a stuck
     cart--loading flag can never freeze the engine. */
  function sendUpdateWhenIdle(payload, giftVariantIds, reRender) {
    if (inFlight) {
      dirty = true;
      return;
    }
    if (nativeCartBusy() && recalcBusyAttempts < 12) {
      recalcBusyAttempts++;
      clearTimeout(recalcTimer);
      recalcTimer = setTimeout(function () {
        sendUpdateWhenIdle(payload, giftVariantIds, reRender);
      }, 250);
      return;
    }
    recalcBusyAttempts = 0;
    recalcTimer = null;
    postUpdate(payload, giftVariantIds, reRender);
  }

  function postUpdate(payload, giftVariantIds, reRender) {
    inFlight = true;

    originalFetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (data) {
            throw { cartError: true, data: data, status: res.status };
          });
        }
        return res.json();
      })
      .then(function (newCart) {
        cart = newCart;

        /* Gifts the API silently dropped (drafted products) */
        if (giftVariantIds.length) {
          var missing = giftVariantIds.filter(function (vid) {
            return !(newCart.items || []).some(function (item) { return String(item.id) === String(vid); });
          });
          if (missing.length) {
            missing.forEach(function (vid) {
              if (unavailableGiftVariants.indexOf(vid) === -1) unavailableGiftVariants.push(vid);
            });
            unavailableGiftLastAttempt = Date.now();
            showToast(str('giftUnavailable'));
          }
        }

        /* Unavailable variants that made it into the cart are available again */
        unavailableGiftVariants = unavailableGiftVariants.filter(function (vid) {
          return !(newCart.items || []).some(function (item) { return String(item.id) === String(vid); });
        });

        updateGiftUnavailableUI();
        finishFlight(newCart, reRender);
      })
      .catch(function (error) {
        if (error && error.cartError && giftVariantIds.length) {
          /* One retry without the gift variants the API rejected */
          var failed = findFailedVariants(payload.updates || {}, giftVariantIds, error.data);
          failed.forEach(function (vid) {
            if (unavailableGiftVariants.indexOf(vid) === -1) unavailableGiftVariants.push(vid);
          });
          unavailableGiftLastAttempt = Date.now();
          showToast(str('giftUnavailable'));
          updateGiftUnavailableUI();

          var retryUpdates = {};
          Object.keys(payload.updates || {}).forEach(function (vid) {
            if (failed.indexOf(vid) === -1) retryUpdates[vid] = payload.updates[vid];
          });

          if (Object.keys(retryUpdates).length) {
            var retryPayload = { updates: retryUpdates };
            if (payload.attributes) retryPayload.attributes = payload.attributes;
            inFlight = false;
            postUpdate(retryPayload, [], reRender);
            return;
          }
        } else {
          console.error('[cart-offers] update failed', error);
        }
        inFlight = false;
        /* The removal didn't land — reveal the gift again so the UI reflects the
           real cart. engineWantsChange stays true, so checkout stays locked and
           the next change re-attempts the removal. */
        pendingGiftRemovals = [];
        applyGiftHides();
        settleDirty();
        updateCheckoutLock();
      });
  }

  function findFailedVariants(updates, giftVariantIds, errorData) {
    if (errorData && typeof errorData.description === 'string') {
      var description = errorData.description.toLowerCase();
      for (var i = 0; i < giftVariantIds.length; i++) {
        if (description.indexOf(String(giftVariantIds[i])) !== -1) return [String(giftVariantIds[i])];
      }
    }
    return giftVariantIds.filter(function (vid) { return updates[vid] && updates[vid] > 0; });
  }

  function finishFlight(newCart, reRender) {
    inFlight = false;
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: newCart }, bubbles: true }));
    if (reRender) {
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    }
    settleDirty();
  }

  function settleDirty() {
    if (dirty) {
      dirty = false;
      scheduleRecalc();
    }
  }

  /* ── UI: toast + gift-unavailable notice ────────────────────────────────── */

  var toastTimer = null;
  var panelMsgTimer = null;

  function showToast(message) {
    if (!message) return;
    // The coupon slide-over (z-index 20060) covers the cart body and the body
    // toast with it. While it's open, surface the message inside the panel so the
    // shopper actually sees it (e.g. "Invalid or inapplicable discount code")
    // instead of it appearing hidden behind the panel.
    var panel = document.querySelector('[data-coupon-panel].is-open');
    if (panel) {
      var panelMsg = panel.querySelector('[data-coupon-panel-message]');
      if (panelMsg) {
        panelMsg.textContent = message;
        panelMsg.removeAttribute('hidden');
        clearTimeout(panelMsgTimer);
        panelMsgTimer = setTimeout(function () {
          panelMsg.setAttribute('hidden', '');
        }, 4000);
        return;
      }
    }
    var toast = document.querySelector('[data-cart-offers-toast]');
    if (!toast) return;
    var text = toast.querySelector('[data-cart-offers-toast-text]');
    if (text) text.textContent = message;
    toast.removeAttribute('hidden');
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
      toast.setAttribute('hidden', '');
    }, 4000);
  }

  /* ── Restricted-product strip (bundle offers) ────────────────────────────
     "Restricted products count max 1" is soft — extra units stay in the cart
     at full price. When the shopper pushes a restricted product's quantity
     past 1 (stepper, PDP, upsell — any path lands here via theme:cart:change)
     surface the offer's restricted_message once through the shared toast
     strip, [[PRODUCT]] replaced with the product's title. The first
     observation only seeds the baseline, so opening a cart that already
     holds 2+ doesn't toast. */
  var restrictedQtyPrev = null;

  function checkRestrictedIncrease(freshCart) {
    if (!freshCart || !freshCart.items) return;
    var offer = null;
    var offers = config.bundleOffers || [];
    for (var i = 0; i < offers.length; i++) {
      if (!bundleOfferUsable(offers[i])) continue;
      if (!offers[i].restrictedMessage) continue;
      if (!(offers[i].restrictedProductIds || []).length) continue;
      offer = offers[i];
      break;
    }
    if (!offer) {
      restrictedQtyPrev = null; // re-seed when an offer becomes usable again
      return;
    }

    var counts = {};
    offer.restrictedProductIds.forEach(function (pid) { counts[String(pid)] = 0; });
    var titles = {};
    freshCart.items.forEach(function (item) {
      var pid = String(item.product_id);
      if (!(pid in counts)) return;
      counts[pid] += item.quantity;
      titles[pid] = item.product_title;
    });

    if (restrictedQtyPrev) {
      for (var pid in counts) {
        var prev = restrictedQtyPrev[pid] || 0;
        if (counts[pid] > 1 && counts[pid] > prev) {
          showToast(offer.restrictedMessage.replace(/\[\[PRODUCT\]\]/g, titles[pid] || ''));
          break;
        }
      }
    }
    restrictedQtyPrev = counts;
  }

  function updateGiftUnavailableUI() {
    var notice = document.querySelector('[data-gift-unavailable]');
    var show = unavailableGiftVariants.length > 0 || bogoOfferDrafted;
    if (notice) {
      if (show) {
        notice.removeAttribute('hidden');
      } else {
        notice.setAttribute('hidden', '');
      }
    }
    if (show) {
      document.body.setAttribute('data-gift-unavailable', 'true');
    } else {
      document.body.removeAttribute('data-gift-unavailable');
    }
  }

  /* ── Checkout lock ──────────────────────────────────────────────────────────
     Checkout must never be reachable while the engine still has gift lines to
     add or remove. Without this, the gap between a trigger product being removed
     and its free gift being removed (a debounce + one cart round-trip) lets a
     shopper check out the gift at its ₹0 marker price. The lock holds whenever a
     recalc is pending, an update is in flight, or the last recalc found gift
     lines that still need reconciling — and lifts only once desired == actual.
     GoKwik's own readiness gate is preserved (its button stays disabled until
     the integration script fires gokwikLoaded). */
  function cartHasGiftLine() {
    return ((cart && cart.items) || []).some(isGiftLine);
  }

  function reconcilePending() {
    if (!config) return false;
    // A native cart write (quantity change / remove) is mid-flight: its result
    // hasn't fired theme:cart:change yet, so the cart on screen — and the cart
    // checkout would carry — is stale. Lock until it settles. (nativeBusy is
    // driven by the cart--loading flag via watchNativeBusy, with a backstop so a
    // stuck flag can't freeze checkout.)
    if (nativeBusy) return true;
    // A product is being added to the cart (drawer offer/bundle/upsell add).
    if (addBusy) return true;
    // A coupon apply/remove is committing + verifying against the server — the
    // discount isn't on the server cart yet, so checkout would be stale.
    if (couponBusy) return true;
    // A write we initiated, or a gift add/remove the last recalc decided on.
    if (inFlight || dirty || engineWantsChange) return true;
    // Hold the lock through the recalc debounce ONLY when the cart actually
    // contains a gift line the recalc might need to drop. Without this guard a
    // plain quantity change on a normal product locks then unlocks ~100ms later,
    // blinking the checkout button on every +/- click.
    return recalcTimer !== null && cartHasGiftLine();
  }

  /* Everything that must finish before the cart is safe to hand to GoKwik: a
     coupon commit, a native quantity/remove write, an engine gift add/remove, or
     a recalc still pending. The checkout click waits on this (openCheckoutWhenSettled). */
  function cartSettling() {
    return couponBusy || nativeBusy || addBusy || inFlight || dirty || engineWantsChange || recalcTimer !== null;
  }

  function updateCheckoutLock() {
    checkoutLocked = reconcilePending();
    document.querySelectorAll('[data-gokwik-checkout], button[name="checkout"]').forEach(function (btn) {
      if (btn.hasAttribute('data-gokwik-checkout')) {
        /* GoKwik button: disabled until GoKwik is ready AND whenever the cart is
           settling (a quantity change / remove / add / coupon is in flight), so it
           can't be clicked against a cart that's about to change. The click-time
           openCheckoutWhenSettled remains as a backstop for any click that slips
           through before this repaints. */
        btn.disabled = !gokwikReady || checkoutLocked;
      } else {
        btn.disabled = checkoutLocked;
      }
    });
  }

  /* Lock checkout the instant a native cart operation starts — not just when it
     finishes. The theme toggles cart--loading on the cart-form wrapper for the
     full duration of every quantity change / remove (lockState → showForm), so
     observing that class lets the lock engage on op-start (closing the
     "remove then immediately click checkout" window) and lift on op-end. No
     polling, no extra requests. An 8s backstop clears the flag if a dropped
     response ever leaves cart--loading stuck, so checkout can't freeze. */
  function watchNativeBusy() {
    var loaders = document.querySelectorAll('[data-cart-loading]');
    if (!loaders.length) return;
    function sync() {
      var busy = false;
      loaders.forEach(function (loader) {
        if (loader.classList.contains('cart--loading')) busy = true;
      });
      if (busy === nativeBusy) return;
      nativeBusy = busy;
      clearTimeout(nativeBusyTimer);
      if (busy) {
        nativeBusyTimer = setTimeout(function () {
          nativeBusy = false;
          updateCheckoutLock();
        }, 8000);
      }
      updateCheckoutLock();
    }
    var observer = new MutationObserver(sync);
    loaders.forEach(function (loader) {
      observer.observe(loader, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* Belt-and-braces: stop a keyboard/Enter submit of the checkout form while the
     gift lines are still being reconciled (the disabled button covers clicks). */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.id === 'cart-form' && checkoutLocked) {
      e.preventDefault();
    }
  }, true);

  /* ── Optimistic gift removal ────────────────────────────────────────────────
     Hide the gift rows the engine has decided to drop so they disappear with the
     product that earned them, instead of lingering until the removal POST + the
     drawer re-render complete. The native cart re-renders the gift back in while
     it's still in the live cart, so a MutationObserver re-applies the hide on
     every re-render until our removal lands and the row is gone for real. */
  function cartHasVariant(vid) {
    return ((cart && cart.items) || []).some(function (item) {
      return String(item.id) === String(vid);
    });
  }

  function applyGiftHides() {
    var container = document.querySelector('[data-line-items]');
    if (!container) return;
    container.querySelectorAll('.cart-line--gift[data-variant-id]').forEach(function (row) {
      var vid = row.getAttribute('data-variant-id');
      if (pendingGiftRemovals.indexOf(vid) !== -1) {
        row.classList.add('cart-line--removing'); // engine is dropping it → hide
      } else if (cartHasVariant(vid)) {
        row.classList.remove('cart-line--removing'); // real, wanted line → show
      }
      /* else: already out of the cart but the re-render hasn't dropped the row
         yet — keep it hidden so a removed gift doesn't flash back. */
    });
  }

  /* The native stepper sets the new quantity optimistically, but a cart
     re-render can briefly paint a stale quantity — Shopify sometimes renders the
     line-items section from a cart replica a beat behind the change.js commit —
     producing a 2 → 1 → 2 flicker. The change.js response we hold in `cart` is
     authoritative, so after any re-render snap each quantity input back to it. */
  function syncQuantities() {
    if (!cart || !cart.items) return;
    var container = document.querySelector('[data-line-items]');
    if (!container) return;
    var qtyByKey = {};
    cart.items.forEach(function (item) { qtyByKey[item.key] = item.quantity; });
    container.querySelectorAll('[data-quantity-input][data-update-cart]').forEach(function (input) {
      var qty = qtyByKey[input.getAttribute('data-update-cart')];
      if (qty != null && String(input.value) !== String(qty)) input.value = qty;
    });
  }

  /* "Add your free gift back" link (rendered hidden by cart-bundle-progress) —
     shown only while a manually-removed bundle freebie's tier is still met. */
  function updateBundleRestoreUI() {
    document.querySelectorAll('[data-bundle-gift-restore]').forEach(function (el) {
      el.hidden = !bundleRestorable;
    });
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-bundle-gift-restore]');
    if (!link) return;
    e.preventDefault();
    var memory = getBundleMemory();
    memory.removed = {};
    setBundleMemory(memory);
    bundleRestorable = false;
    updateBundleRestoreUI();
    scheduleRecalc();
  });

  function watchLineItems() {
    var container = document.querySelector('[data-line-items]');
    if (!container) return;
    new MutationObserver(function () {
      if (pendingGiftRemovals.length) applyGiftHides();
      updateBundleRestoreUI();
      syncQuantities();
      revealCartImages();
    }).observe(container, { childList: true, subtree: true });
  }

  /* Reveal AJAX-injected cart images. The theme's render 'image' snippet wraps
     each image in a .loading-shimmer placeholder (.loading-shimmer img is
     opacity:0) and clears it via a document 'load' listener + a one-off page-load
     scan (theme.js). Neither re-runs after the drawer re-renders, so a
     cache-complete <img> — injected already `complete`, firing no fresh load
     event — stays hidden behind the shimmer (the blank placeholder box). After a
     re-render, clear the shimmer on any already-complete image and bind a
     one-shot load/error reveal for the rest. */
  function revealCartImages() {
    var drawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (!drawer) return;
    drawer.querySelectorAll('.loading-shimmer img').forEach(function (img) {
      var wrapper = img.closest('.loading-shimmer');
      if (!wrapper) return;
      if (img.complete && img.naturalWidth > 0) {
        wrapper.classList.remove('loading-shimmer');
      } else if (!img.dataset.shimmerBound) {
        img.dataset.shimmerBound = '1';
        var reveal = function () { wrapper.classList.remove('loading-shimmer'); };
        img.addEventListener('load', reveal, { once: true });
        img.addEventListener('error', reveal, { once: true });
      }
    });
  }

  /* ── Coupons ────────────────────────────────────────────────────────────── */

  /* Apply/remove uses the platform-native Cart API discount support:
     POST /cart/update.js { discount: "CODE_A,CODE_B" } (multi-code, the same
     mechanism Horizon's cart-discount component is built on). Shopify accepts
     unknown codes silently, so applies are verified against a fresh cart. */
  var couponBusy = false;
  var lottiePromise = null;

  /* Coupon apply/remove commits server-side via /cart/update.js + a verifying
     /cart.js. Until that settles the discount isn't on the server cart, so
     handing off to GoKwik would open checkout against a stale cart (the "Your
     cart has been updated" banner + skeleton order summary). Route every flip
     through here so the checkout lock engages the moment an apply/remove starts
     and lifts only once it resolves. */
  function setCouponBusy(state) {
    couponBusy = state;
    updateCheckoutLock();
  }

  function getAppliedCodes(fromCart) {
    if (!fromCart) return [];
    var codes = [];
    function collect(title) {
      if (title && codes.indexOf(title) === -1) codes.push(title);
    }
    (fromCart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        collect(allocation.discount_application && allocation.discount_application.title);
      });
    });
    (fromCart.cart_level_discount_applications || []).forEach(function (discount) {
      collect(discount.title);
    });
    return codes;
  }

  function isDiscountApplied(fromCart, code) {
    var target = (code || '').trim().toUpperCase();
    return getAppliedCodes(fromCart).some(function (title) {
      return title.toUpperCase() === target;
    });
  }

  function postDiscount(payload) {
    return originalFetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: payload })
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) throw new Error(data.message || data.description || res.statusText);
        return data;
      });
    });
  }

  function refreshCart() {
    return originalFetch(rootUrl() + 'cart.js').then(function (res) { return res.json(); });
  }

  function announceCart(newCart) {
    cart = newCart;
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: newCart }, bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }

  /* Shopify keeps a manually-entered discount CODE on the cart token even after the last
     line item is removed, then silently re-applies it to the next item added — so a coupon
     from an emptied cart leaks into the next one. Clearing the code while the cart is empty
     does NOT stick (Shopify ignores discount writes when there are no line items), so we
     instead remember that the cart was emptied (persisted so it survives a reload too) and
     strip any leftover code the moment items reappear — which is when the write reliably
     applies. A coupon the shopper deliberately (re)applies clears the flag, so it is kept.
     Only user `discount_code` discounts are touched; automatic/admin discounts and cart
     attributes/notes (GLOW CREDITS, GoKwik) are never affected. */
  var EMPTIED_FLAG_KEY = 'asayaCartEmptied';
  var staleCouponClearing = false;

  function setEmptiedFlag(on) {
    try {
      if (on) localStorage.setItem(EMPTIED_FLAG_KEY, '1');
      else localStorage.removeItem(EMPTIED_FLAG_KEY);
    } catch (e) { /* storage unavailable — in-session flow still works via the cart events */ }
  }
  function getEmptiedFlag() {
    try { return localStorage.getItem(EMPTIED_FLAG_KEY) === '1'; }
    catch (e) { return false; }
  }

  // User-entered coupon codes only — ignores automatic/admin (auto-apply) discounts.
  function getUserCouponCodes(fromCart) {
    if (!fromCart) return [];
    var codes = [];
    function collect(app) {
      if (app && app.type === 'discount_code' && app.title && codes.indexOf(app.title) === -1) {
        codes.push(app.title);
      }
    }
    (fromCart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (a) { collect(a.discount_application); });
    });
    (fromCart.cart_level_discount_applications || []).forEach(collect);
    return codes;
  }

  function reconcileStaleCoupon(currentCart) {
    if (!currentCart) return;
    if (currentCart.item_count === 0) {
      setEmptiedFlag(true); // remember the empty so we can strip a leftover code on refill
      return;
    }
    if (!getEmptiedFlag() || staleCouponClearing) return;
    if (!getUserCouponCodes(currentCart).length) {
      setEmptiedFlag(false); // refilled with no leftover code — nothing to strip
      return;
    }
    // Cart was emptied and refilled, yet a user code is still attached: strip it now.
    staleCouponClearing = true;
    setEmptiedFlag(false);
    postDiscount('')
      .then(refreshCart)
      .then(function (newCart) {
        staleCouponClearing = false;
        announceCart(newCart);
      })
      .catch(function () {
        staleCouponClearing = false;
        setEmptiedFlag(true); // failed — retry on the next cart change
        console.warn('[cart-offers] could not clear stale coupon after cart refill');
      });
  }

  function applyDiscountCode(code, button) {
    var sanitized = (code || '').trim().replace(/[^\w-]/g, '');
    if (!sanitized) {
      showToast(str('couponEnterCode'));
      return;
    }
    if (!cart || cart.item_count === 0) {
      showToast(str('couponEmptyCart'));
      return;
    }
    if (couponBusy) return;
    setEmptiedFlag(false); // deliberate apply — this code is intentional, never strip it
    setCouponBusy(true);
    if (button) button.classList.add('is-loading');
    showToast(str('applying'));

    var codes = getAppliedCodes(cart);
    if (!codes.some(function (c) { return c.toUpperCase() === sanitized.toUpperCase(); })) {
      codes.push(sanitized);
    }

    whenNativeIdle(function () {
    postDiscount(codes.join(','))
      .then(refreshCart)
      .then(function (verified) {
        setCouponBusy(false);
        if (!isDiscountApplied(verified, sanitized)) {
          if (button) button.classList.remove('is-loading');
          showToast(str('couponInvalid'));
          return;
        }
        announceCart(verified); // re-render replaces the button → clears the spinner
        showToast(str('couponApplied'));
        playConfetti();
      })
      .catch(function (err) {
        setCouponBusy(false);
        if (button) button.classList.remove('is-loading');
        showToast(str('couponError'));
        console.error('[cart-offers] apply discount failed', err);
      });
    });
  }

  function removeDiscountCode(code, button) {
    if (couponBusy) return;
    setCouponBusy(true);
    if (button) button.classList.add('is-loading');
    showToast(str('removing'));

    var target = (code || '').trim().toUpperCase();
    var remaining = getAppliedCodes(cart).filter(function (c) {
      return c.toUpperCase() !== target;
    });

    whenNativeIdle(function () {
    postDiscount(remaining.length ? remaining.join(',') : '')
      .then(refreshCart)
      .then(function (newCart) {
        setCouponBusy(false);
        announceCart(newCart); // re-render replaces the button → clears the spinner
        showToast(str('couponRemoved'));
      })
      .catch(function (err) {
        setCouponBusy(false);
        if (button) button.classList.remove('is-loading');
        showToast(str('couponError'));
        console.error('[cart-offers] remove discount failed', err);
      });
    });
  }

  function setCouponPanelOpen(open) {
    var panel = document.querySelector('[data-coupon-panel]');
    if (!panel) return;
    if (open) {
      panel.removeAttribute('hidden');
      void panel.offsetHeight; // reflow so the slide-in transition runs
      panel.classList.add('is-open');
      var input = panel.querySelector('[data-coupon-input]');
      if (input) input.focus();
    } else {
      panel.classList.remove('is-open');
      setTimeout(function () { panel.setAttribute('hidden', ''); }, 250);
    }
  }

  function copyCode(code) {
    function fallbackCopy() {
      var input = document.createElement('input');
      input.value = code;
      document.body.appendChild(input);
      input.select();
      try { document.execCommand('copy'); } catch (e) { /* best effort */ }
      input.remove();
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
    showToast(str('copied'));
  }

  /* Confetti on coupon success — Lottie player + animation JSON load lazily,
     only when first needed. Decorative: all failures are swallowed. */
  function ensureLottie() {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (lottiePromise) return lottiePromise;
    lottiePromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/bodymovin/5.7.6/lottie.min.js';
      script.onload = function () { resolve(window.lottie); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return lottiePromise;
  }

  function playConfetti() {
    var holder = document.querySelector('[data-cart-confetti]');
    if (!holder || !holder.getAttribute('data-confetti-src')) return;
    ensureLottie()
      .then(function (lottie) {
        var animation = lottie.loadAnimation({
          container: holder,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          path: holder.getAttribute('data-confetti-src')
        });
        animation.addEventListener('complete', function () {
          animation.destroy();
          holder.innerHTML = '';
        });
      })
      .catch(function () { /* decorative only */ });
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target.closest) return;

    var applyBtn = target.closest('[data-coupon-apply]');
    if (applyBtn) {
      applyDiscountCode(applyBtn.getAttribute('data-code'), applyBtn);
      return;
    }

    var removeBtn = target.closest('[data-coupon-remove]');
    if (removeBtn) {
      removeDiscountCode(removeBtn.getAttribute('data-code'), removeBtn);
      return;
    }

    if (target.closest('[data-coupon-panel-open]')) {
      setCouponPanelOpen(true);
      return;
    }

    if (target.closest('[data-coupon-panel-close]')) {
      setCouponPanelOpen(false);
      return;
    }

    var submitBtn = target.closest('[data-coupon-submit]');
    if (submitBtn) {
      var panel = submitBtn.closest('[data-coupon-panel]');
      var input = panel && panel.querySelector('[data-coupon-input]');
      if (input) {
        applyDiscountCode(input.value, submitBtn);
        input.value = '';
      }
      return;
    }

    var copyBtn = target.closest('[data-code-copy]');
    if (copyBtn) {
      copyCode(copyBtn.getAttribute('data-code'));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    if (!e.target.matches || !e.target.matches('[data-coupon-input]')) return;
    e.preventDefault();
    applyDiscountCode(e.target.value);
    e.target.value = '';
  });

  /* ── Announcement ribbon ────────────────────────────────────────────────── */

  /* Static drawer-shell element: rotation set up once, visibility follows the
     cart (hidden while empty). */
  function initAnnouncementRibbon() {
    var ribbon = document.querySelector('[data-cart-announcement]');
    if (!ribbon || ribbon.getAttribute('data-initialized') === 'true') return;
    ribbon.setAttribute('data-initialized', 'true');

    var items = ribbon.querySelectorAll('[data-announcement-item]');
    if (!items.length) return;

    var index = 0;
    items[0].classList.add('is-active');

    if (items.length < 2 || ribbon.getAttribute('data-autoplay') !== 'true') return;

    var speed = (parseInt(ribbon.getAttribute('data-rotation-speed'), 10) || 3) * 1000;
    var timer = null;

    function rotate() {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }

    function start() {
      stop();
      timer = setInterval(rotate, speed);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    ribbon.addEventListener('mouseenter', stop);
    ribbon.addEventListener('mouseleave', start);
    ribbon.addEventListener('focusin', stop);
    ribbon.addEventListener('focusout', function (e) {
      if (!ribbon.contains(e.relatedTarget)) start();
    });

    start();
  }

  function syncAnnouncementVisibility() {
    var ribbon = document.querySelector('[data-cart-announcement]');
    if (!ribbon) return;
    if (cart && cart.item_count > 0) {
      ribbon.removeAttribute('hidden');
    } else {
      ribbon.setAttribute('hidden', '');
    }
  }

  /* Header count badge (Figma 24:358) — static drawer-shell element, kept in
     sync with the cart on every change */
  function syncHeaderCount() {
    var badge = document.querySelector('[data-cart-count-badge]');
    if (badge && cart) badge.textContent = cart.item_count;
  }

  /* The theme's CartItems.updateTotal appends the ISO currency code ("INR")
     after the footer price; design wants the symbol only. theme.js can't be
     edited, so the suffix is stripped whenever the element re-renders. The
     equality guard stops the observer from re-triggering itself. */
  function stripCurrencyCode() {
    var el = document.querySelector('[data-cart-final]');
    if (!el) return;
    var cleaned = el.textContent.replace(/\s*[A-Z]{3}\s*$/, '');
    if (cleaned !== el.textContent) el.textContent = cleaned;
  }

  function watchFinalPrice() {
    var el = document.querySelector('[data-cart-final]');
    if (!el) return;
    new MutationObserver(stripCurrencyCode).observe(el, { childList: true, characterData: true, subtree: true });
    stripCurrencyCode();
  }

  /* ── Drawer helpers ─────────────────────────────────────────────────────── */

  function isDrawerOpen() {
    var drawer = document.querySelector('[data-drawer="drawer-cart"]');
    return !!(drawer && drawer.classList.contains('drawer--visible'));
  }

  function openDrawer() {
    var drawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (!drawer || isDrawerOpen()) return;
    drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: true }));
  }

  /* Pre-warm the drawer so the FIRST cart-icon click opens instantly. The theme
     lazy-loads on the first theme:drawer:open: when its CartItems.cart is still
     null it fetches /cart.js AND the drawer section in sequence before showing
     content (slow). Firing theme:cart:reload once, while the drawer is closed,
     runs that same work now — populating CartItems.cart and rendering the line
     items — so the first open finds everything ready. Idempotent and invisible
     (no drawer:open dispatched). */
  function warmCartDrawer() {
    if (isDrawerOpen()) return;
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }

  /* ?open_cart=1 — landed here from the killed cart page: open the drawer.
     The drawer's event listener attaches during theme section init, so retry
     briefly; isDrawerOpen() keeps the retries idempotent. */
  function handleOpenCartParam() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('open_cart') !== '1') return;

    var attempts = 0;
    (function tryOpen() {
      openDrawer();
      attempts++;
      if (!isDrawerOpen() && attempts < 10) {
        setTimeout(tryOpen, 200);
      }
    })();

    params.delete('open_cart');
    var query = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : ''));
  }

  /* ── GoKwik checkout ────────────────────────────────────────────────────── */

  /* The drawer's checkout button (settings.goEnable) starts disabled and is
     enabled once the GoKwik merchant integration script is ready. Clicking
     hands off to onCheckoutClick, which GoKwik defines globally. */
  function enableGokwikCheckout() {
    gokwikReady = true;
    updateCheckoutLock();
  }

  window.addEventListener('gokwikLoaded', enableGokwikCheckout);

  /* Open GoKwik only once the cart is fully reconciled. Applying a coupon (or any
     cart change) can make the engine add/remove a gift on the NEXT recalc — a
     write that lands a beat after the visible UI settles. If GoKwik opens in that
     window it captures a cart that then mutates underneath it, and shows "Your
     cart has been updated" + a skeleton order summary. So: flush any pending
     recalc immediately, then wait for every in-flight/pending operation to clear
     before handing off. No extra cart fetch — GoKwik reads the server cart itself
     once we let it open. A backstop proceeds anyway so the shopper is never stuck. */
  function openCheckoutWhenSettled(button) {
    if (typeof window.onCheckoutClick !== 'function') {
      console.error('[cart-offers] onCheckoutClick is not available — check GoKwik settings');
      return;
    }
    if (recalcTimer !== null) {
      clearTimeout(recalcTimer);
      recalcTimer = null;
      recalc(); // converge now instead of waiting out the debounce
    }
    if (!cartSettling()) {
      window.onCheckoutClick(button);
      return;
    }
    button.classList.add('is-loading');
    var attempts = 0;
    (function wait() {
      if (!cartSettling() || attempts++ > 80) { // ~10s backstop
        button.classList.remove('is-loading');
        window.onCheckoutClick(button);
        return;
      }
      setTimeout(wait, 120);
    })();
  }

  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('[data-gokwik-checkout]');
    if (!button || button.disabled) return;
    openCheckoutWhenSettled(button);
  });

  /* Quantity guard — the moment any line's +/− (or trash) is clicked, disable
     EVERY line's quantity buttons until the update settles. This serializes cart
     mutations: it stops a second item being changed while the first is still in
     flight, which otherwise lets one update's full-list re-render paint a stale
     quantity for the other (the 10→9→10 flicker) and can trigger the body-less
     409 → "All available stock is in cart". Bubble-phase so the theme's own
     handler runs first (increments + sends the update); the theme then
     re-renders the whole line-item list on success AND on error, restoring
     fresh, enabled buttons. Variant dropdowns are left alone. */
  document.addEventListener('click', function (e) {
    var step = e.target.closest && e.target.closest('[data-increase-quantity], [data-decrease-quantity]');
    if (!step || !step.closest('[data-cart-item]')) return;
    document.querySelectorAll('[data-drawer="drawer-cart"] .cart-line__qty-btn').forEach(function (btn) {
      btn.disabled = true;
    });
  });

  /* ── Drawer adds (BOGO offers, bundles, upsells, public API) ────────────── */

  /* Adds through the native cart API, then refreshes state + drawer. The
     resulting theme:cart:change runs the full offer recalc. */
  function addItems(items) {
    setAddBusy(true);
    return originalFetch(rootUrl() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (data) {
            throw new Error(data.description || data.message || res.statusText);
          });
        }
        return res.json();
      })
      .then(function () {
        return originalFetch(rootUrl() + 'cart.js').then(function (res) { return res.json(); });
      })
      .then(function (newCart) {
        cart = newCart;
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: newCart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        return newCart;
      })
      .then(function (newCart) {
        setAddBusy(false);
        return newCart;
      }, function (err) {
        setAddBusy(false);
        throw err;
      });
  }

  document.addEventListener('click', function (e) {
    var single = e.target.closest && e.target.closest('[data-offer-add]');
    var bundle = e.target.closest && e.target.closest('[data-offer-add-bundle]');
    if (!single && !bundle) return;

    var button = single || bundle;
    if (button.disabled) return;

    var items = [];
    if (single) {
      var variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;
      items = [{ id: variantId, quantity: 1 }];
    } else {
      try {
        items = JSON.parse(button.getAttribute('data-bundle-variants') || '[]').map(function (vid) {
          return { id: vid, quantity: 1 };
        });
      } catch (err) {
        return;
      }
    }
    if (!items.length) return;

    button.disabled = true;
    button.classList.add('is-loading');
    whenNativeIdle(function () {
      addItems(items).then(function () {
        /* Clear the spinner on success too. In-cart carousel buttons get
           replaced by the re-render (clearing on the detached node is a no-op),
           but the empty-cart upsell lives in the static [data-cart-empty]
           container that's only shown/hidden — never re-rendered — so without
           this its button stays stuck spinning the next time the cart empties. */
        button.disabled = false;
        button.classList.remove('is-loading');
      }).catch(function (err) {
        button.disabled = false;
        button.classList.remove('is-loading');
        showToast(err && err.message ? err.message : str('couponError'));
        console.error('[cart-offers] offer add failed', err);
      });
    });
  });

  /* Remove a manually-added offer reward (BOGO Remove / Remove Bundle). Sets the
     variant(s) to qty 0; manual BOGO only keeps offer products that are IN the
     cart, so once removed the recalc won't re-add them. */
  function removeOfferVariants(variantIds) {
    var updates = {};
    variantIds.forEach(function (vid) { updates[String(vid)] = 0; });
    return originalFetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (res) { return res.json(); })
      .then(function (newCart) {
        cart = newCart;
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: newCart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        return newCart;
      });
  }

  document.addEventListener('click', function (e) {
    var single = e.target.closest && e.target.closest('[data-offer-remove]');
    var bundle = e.target.closest && e.target.closest('[data-offer-remove-bundle]');
    if (!single && !bundle) return;

    var button = single || bundle;
    if (button.disabled) return;

    var ids = [];
    if (single) {
      var variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;
      ids = [variantId];
    } else {
      try {
        ids = JSON.parse(button.getAttribute('data-bundle-variants') || '[]');
      } catch (err) {
        return;
      }
    }
    if (!ids.length) return;

    button.disabled = true;
    button.classList.add('is-loading');
    whenNativeIdle(function () {
      removeOfferVariants(ids).catch(function (err) {
        button.disabled = false;
        button.classList.remove('is-loading');
        showToast(err && err.message ? err.message : str('couponError'));
        console.error('[cart-offers] offer remove failed', err);
      });
    });
  });

  /* ── Claim gift interactions ────────────────────────────────────────────── */

  /* The claim strip re-renders with the drawer, so handlers are delegated.
     Claiming only writes the choice; the recalc applies it to the cart. */
  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest && e.target.closest('[data-claim-add]');
    var removeBtn = e.target.closest && e.target.closest('[data-claim-remove]');
    if (!addBtn && !removeBtn) return;

    var button = addBtn || removeBtn;
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    /* Spinner on the clicked button until the strip re-renders (matches the
       upsell/cart loaders). is-loading also blocks re-clicks (pointer-events). */
    button.classList.add('is-loading');

    var store = getClaimedGifts() || {};
    if (addBtn) {
      store[String(variantId)] = { q: 1, t: Date.now() };
    } else {
      delete store[String(variantId)];
    }
    setClaimedGifts(store);
    /* Apply now (skip the coalescing debounce) — a claim click is deliberate, so
       the spinner shouldn't sit through the 250ms window. If a prior update is
       in flight, recalc sets the dirty flag and runs again when it settles. */
    clearTimeout(recalcTimer);
    recalc();
  });

  /* Scroll progress under the claim strip (scroll doesn't bubble → capture) */
  document.addEventListener('scroll', function (e) {
    var track = e.target;
    if (!track || !track.hasAttribute || !track.hasAttribute('data-claim-track')) return;
    var fill = track.parentElement && track.parentElement.querySelector('[data-claim-progress]');
    if (!fill) return;
    var scrollable = track.scrollWidth - track.clientWidth;
    fill.style.width = scrollable > 0 ? (track.scrollLeft / scrollable) * 100 + '%' : '100%';
  }, { passive: true, capture: true });

  /* Any in-page /cart link opens the drawer instead of navigating */
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href*="/cart"]');
    if (!link || e.ctrlKey || e.metaKey || e.shiftKey || link.target === '_blank') return;
    try {
      var path = new URL(link.href, window.location.origin).pathname;
      if (path === cartRoute()) {
        e.preventDefault();
        openDrawer();
      }
    } catch (err) { /* invalid URL — let it navigate */ }
  });

  /* ── Open cart drawer on Add to Cart (opt-in setting) ────────────────────────
     On ATC success with the cart closed, the theme shows an add-confirmation
     popdown (theme.js dispatches theme:cart:popdown, then its own document-level
     handler renders the popdown). When the merchant enables "Open cart drawer on
     add to cart", suppress that popdown and open the drawer instead. A capture-
     phase listener runs before theme.js's bubble-phase one, so
     stopImmediatePropagation blocks the popdown render without touching the
     compiled theme.js. theme:cart:reload refreshes the drawer with the new line. */
  document.addEventListener('theme:cart:popdown', function (e) {
    if (!config || !config.openDrawerOnAdd) return;
    e.stopImmediatePropagation();
    /* The theme's ATC handler fires this popdown SYNCHRONOUSLY but refreshes the
       cart (updateHeaderTotal → GET /cart.js → theme:cart:change) asynchronously,
       so right now CartItems.cart is still the pre-add cart. Firing reload here
       would render from that stale cart — showing the empty state on the first
       add (item_count still 0). Fetch the fresh cart ourselves, announce it via
       theme:cart:change so CartItems.cart is current, THEN reload (loadForm) and
       open. openDrawer sets cartOpen=true, which suppresses the theme's own
       change-driven re-render, so this refresh must happen before we open. */
    originalFetch(rootUrl() + 'cart.js')
      .then(function (res) { return res.json(); })
      .then(function (freshCart) {
        cart = freshCart;
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: freshCart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        openDrawer();
      })
      .catch(function () {
        /* Network hiccup — fall back to the plain reload + open. */
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        openDrawer();
      });
  }, true);

  /* ── Stuck Add-to-Cart watchdog ─────────────────────────────────────────────
     The theme adds via themeVendor.axios.post('/cart/add.js') with no timeout,
     so a network stall AFTER the server has processed the add leaves the button
     spinning forever — its success/error handlers never run, and a page reload
     is the only escape (by which point the item is already in the cart). Watch
     each ATC button after it's clicked: if it's still spinning past a grace
     period, reconcile against the real cart. If the add actually landed
     (item_count grew) treat it as success — reset the button and refresh/open the
     drawer; otherwise just reset the button. State is reset through Alpine (the
     same reactive flag the theme toggles) with a direct class fallback, so we
     recover both the Alpine and web-component add buttons without editing
     theme.js or fighting the :class binding. */
  var ATC_STUCK_MS = 8000;
  var atcTimers = new WeakMap();

  function resetAddButton(button) {
    try {
      if (window.Alpine && typeof window.Alpine.$data === 'function') {
        var scope = window.Alpine.$data(button);
        if (scope && typeof scope.state !== 'undefined') scope.state = 0; // INITIAL
      }
    } catch (e) { /* best effort — the class fallback below still recovers it */ }
    button.classList.remove('loading', 'has-success');
    button.disabled = false;
  }

  function reconcileStuckButton(button, priorCount) {
    if (!button.classList.contains('loading')) return; // recovered on its own
    originalFetch(rootUrl() + 'cart.js')
      .then(function (res) { return res.json(); })
      .then(function (freshCart) {
        resetAddButton(button);
        if (freshCart.item_count > priorCount) {
          /* The add landed despite the hung request — surface it like a normal
             success so the shopper isn't left thinking it failed. */
          cart = freshCart;
          document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: freshCart }, bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
          if (config && config.openDrawerOnAdd) openDrawer();
        }
      })
      .catch(function () { resetAddButton(button); });
  }

  /* State-machine ATC buttons uniquely carry a .btn-state-loading span (PDP,
     grid quick-add, quickview) — key off it so the watchdog isn't tied to any
     one markup or the data-add-to-cart attribute. */
  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('button');
    if (!button || !button.querySelector('.btn-state-loading')) return;
    var priorCount = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
    clearTimeout(atcTimers.get(button));
    atcTimers.set(button, setTimeout(function () {
      reconcileStuckButton(button, priorCount);
    }, ATC_STUCK_MS));
  });

  /* ── Public API ─────────────────────────────────────────────────────────── */

  /* Add through the native cart API so all offer logic fires on the resulting
     theme:cart:change, then re-render and open the drawer — for third-party
     scripts and app blocks. */
  window.cartAddToCart = function (variantId, quantity) {
    return addItems([{ id: String(variantId), quantity: Number(quantity) || 1 }]).then(function (newCart) {
      openDrawer();
      return newCart;
    });
  };

  /* ── Bootstrap ──────────────────────────────────────────────────────────── */

  document.addEventListener('theme:cart:change', function (e) {
    if (e.detail && e.detail.cart) {
      cart = e.detail.cart;
      checkRestrictedIncrease(cart);
      reconcileStaleCoupon(cart);
      syncAnnouncementVisibility();
      syncHeaderCount();
      scheduleRecalc();
      /* Lock immediately — before the debounced recalc runs — so the window
         right after a trigger product is removed (gift still in cart) is closed. */
      updateCheckoutLock();
    }
  });

  /* Test-mode activation: visiting any page with ?bundle_test=1 tags the cart
     with the _bundle_test attribute, unlocking testMode bundle offers here AND
     in the pricing function (which reads the same attribute server-side). */
  function activateBundleTestMode() {
    var wantsTestMode;
    try {
      wantsTestMode = new URLSearchParams(window.location.search).get('bundle_test') === '1';
    } catch (e) {
      wantsTestMode = false;
    }
    if (!wantsTestMode) return Promise.resolve();
    return originalFetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _bundle_test: '1' } })
    })
      .then(function (res) { return res.json(); })
      .catch(function () { /* cart unreachable — normal init continues */ });
  }

  function init() {
    config = readConfig();
    handleOpenCartParam();
    initAnnouncementRibbon();
    watchFinalPrice();
    watchLineItems();
    watchNativeBusy();
    if (typeof window.onCheckoutClick === 'function') {
      enableGokwikCheckout(); // GoKwik loaded before this script ran
    }
    if (!config) return; // cart page / config not rendered: redirect + links only

    activateBundleTestMode()
      .then(function () { return originalFetch(rootUrl() + 'cart.js'); })
      .then(function (res) { return res.json(); })
      .then(function (initialCart) {
        cart = initialCart;
        checkRestrictedIncrease(initialCart); // seeds the baseline, never toasts on first sight
        reconcileStaleCoupon(initialCart);
        syncAnnouncementVisibility();
        syncHeaderCount();
        scheduleRecalc();
        updateCheckoutLock();
      })
      .catch(function (e) {
        console.error('[cart-offers] could not fetch cart', e);
      });

    // Warm the drawer once the page is idle so the first cart-icon click is instant.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(warmCartDrawer, { timeout: 2500 });
    } else {
      setTimeout(warmCartDrawer, 1200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* ============================================================
   ASAYA — LOCKED GIFTS IN EXISTING CART-FREEBIES SECTION
   ------------------------------------------------------------
   SAFE UI-ONLY ADD-ON

   - Does NOT create another gift section
   - Does NOT modify the Shopify cart
   - Does NOT call cart/add.js
   - Does NOT call cart/change.js
   - Does NOT call cart/update.js
   - Does NOT touch GoKwik
   - Does NOT touch existing gift engine
   - Uses the existing .cart-freebies section
============================================================ */

(function () {
  'use strict';

  var STYLE_ID = 'asaya-existing-locked-gifts-css';

  var config = null;

  var variantCache = {};

  var renderTimer = null;

  var drawerObserver = null;


  /* ============================================================
     READ EXISTING OFFER CONFIG
  ============================================================ */

  function readOfferConfig() {

    var el =
      document.querySelector(
        '[data-cart-offers-config]'
      );

    if (!el) {
      return null;
    }


    try {

      return JSON.parse(
        el.textContent
      );

    } catch (error) {

      console.warn(
        '[locked-gifts] offer config could not be read'
      );

      return null;

    }

  }


  /* ============================================================
     GET ALL UNIQUE TIER GIFT VARIANT IDS

     Example:

     Tier 1:
       Gift A

     Tier 2:
       Gift A
       Gift B

     Result:
       A
       B

  ============================================================ */

  // function getConfiguredGiftIds() {

  //   if (
  //     !config ||
  //     !Array.isArray(config.giftRules)
  //   ) {
  //     return [];
  //   }


  //   var ids = [];


  //   config.giftRules.forEach(
  //     function (rule) {

  //       (rule.gifts || []).forEach(
  //         function (variantId) {

  //           var id =
  //             String(variantId);


  //           if (
  //             ids.indexOf(id) === -1
  //           ) {

  //             ids.push(id);

  //           }

  //         }
  //       );

  //     }
  //   );


  //   return ids;

  // }
  function getConfiguredGiftIds() {

  var ids = [];


  function addGiftId(
    variantId
  ) {

    if (
      variantId === null ||
      variantId === undefined ||
      variantId === ''
    ) {
      return;
    }


    var id =
      String(variantId);


    if (
      ids.indexOf(id) === -1
    ) {

      ids.push(id);

    }

  }


  if (!config) {
    return ids;
  }


  /*
    NORMAL TIER GIFTS
  */

  if (
    Array.isArray(config.giftRules)
  ) {

    config.giftRules.forEach(
      function (rule) {

        (rule.gifts || []).forEach(
          function (variantId) {

            addGiftId(
              variantId
            );

          }
        );

      }
    );

  }


  /*
    BUNDLE OFFER GIFTS

    Bundle offers use:
      tier.freebieVariantIds
      tier.valueGiftVariantIds

    Add every bundle gift so gifts that
    haven't been unlocked yet are shown
    as locked placeholders.
  */

  if (
    Array.isArray(config.bundleOffers)
  ) {

    config.bundleOffers.forEach(
      function (offer) {

        if (
          !Array.isArray(offer.tiers)
        ) {
          return;
        }


        offer.tiers.forEach(
          function (tier) {

            (tier.freebieVariantIds || [])
              .forEach(
                function (variantId) {

                  addGiftId(
                    variantId
                  );

                }
              );


            (tier.valueGiftVariantIds || [])
              .forEach(
                function (variantId) {

                  addGiftId(
                    variantId
                  );

                }
              );

          }
        );

      }
    );

  }


  return ids;

}


  /* ============================================================
     GET CURRENT EXISTING GIFT SECTION
  ============================================================ */

  function getGiftSection() {

    return document.querySelector(
      '[data-cart-freebies-section]'
    );

  }


  function getGiftList() {

    var section =
      getGiftSection();


    if (!section) {
      return null;
    }


    return section.querySelector(
      '[data-cart-freebies-list]'
    );

  }


  /* ============================================================
     CHECK IF REAL UNLOCKED GIFT ALREADY EXISTS

     Your current Liquid renders real earned gifts with:

       .cart-line--gift
       data-variant-id="..."

     We use that as the source of truth.

     This is MUCH safer than recalculating tiers separately.
  ============================================================ */

  function hasRealGift(
    variantId
  ) {

    var list =
      getGiftList();


    if (!list) {
      return false;
    }


    return !!list.querySelector(
      '.cart-line--gift[data-variant-id="' +
      String(variantId) +
      '"]'
    );

  }


  /* ============================================================
     COUNT REAL UNLOCKED GIFTS
  ============================================================ */

  function getRealUnlockedCount() {

    var list =
      getGiftList();


    if (!list) {
      return 0;
    }


    return list.querySelectorAll(
      '.cart-line--gift[data-variant-id]'
    ).length;

  }


  /* ============================================================
     UPDATE EXISTING "X UNLOCKED" BADGE
  ============================================================ */

  function updateUnlockedBadge() {

    var section =
      getGiftSection();


    if (!section) {
      return;
    }


    var badge =
      section.querySelector(
        '[data-cart-freebies-unlocked]'
      );


    if (!badge) {
      return;
    }


    badge.textContent =
      getRealUnlockedCount() +
      ' UNLOCKED';

  }


  /* ============================================================
     LOAD VARIANT INFO

     GET request only.

     Does NOT mutate cart.
  ============================================================ */

 /* ============================================================
   LOAD GIFT PRODUCT BY VARIANT ID
   Uses PRODUCT featured image, not variant image
============================================================ */

var asayaProductsPromise = null;

function getAllStoreProducts() {
  if (asayaProductsPromise) {
    return asayaProductsPromise;
  }

  asayaProductsPromise = window
    .fetch('/products.json?limit=250', {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
    .then(function (response) {
      if (!response.ok) {
        throw new Error('Products request failed');
      }

      return response.json();
    })
    .then(function (data) {
      return data.products || [];
    })
    .catch(function (error) {
      console.warn(
        '[locked-gifts] products.json failed',
        error
      );

      return [];
    });

  return asayaProductsPromise;
}


function loadVariant(variantId) {
  var id = String(variantId);

  if (variantCache[id]) {
    return Promise.resolve(
      variantCache[id]
    );
  }

  return getAllStoreProducts()
    .then(function (products) {
      var matchedProduct = null;
      var matchedVariant = null;

      /*
        Find the PRODUCT that owns
        this configured gift variant.
      */
      for (
        var p = 0;
        p < products.length;
        p++
      ) {
        var product =
          products[p];

        var variants =
          product.variants || [];

        for (
          var v = 0;
          v < variants.length;
          v++
        ) {
          if (
            String(variants[v].id) === id
          ) {
            matchedProduct =
              product;

            matchedVariant =
              variants[v];

            break;
          }
        }

        if (matchedProduct) {
          break;
        }
      }


      if (!matchedProduct) {
        throw new Error(
          'Gift product not found for variant ' +
          id
        );
      }


      /*
        PRODUCT featured/main image
        --------------------------------
        We deliberately do NOT use:
        matchedVariant.featured_image
      */

      var productImage = '';


      /*
        First preference:
        product.image
      */
      if (
        matchedProduct.image &&
        matchedProduct.image.src
      ) {
        productImage =
          matchedProduct.image.src;
      }


      /*
        Second preference:
        first product image
      */
      if (
        !productImage &&
        matchedProduct.images &&
        matchedProduct.images.length
      ) {
        var firstImage =
          matchedProduct.images[0];

        if (
          typeof firstImage === 'string'
        ) {
          productImage =
            firstImage;
        } else {
          productImage =
            firstImage.src ||
            '';
        }
      }


      var data = {
        id: id,

        title:
          matchedProduct.title ||
          'Free Gift',

        /*
          Product main image
        */
        image:
          productImage,

        price:
  Math.round(
    parseFloat(
      matchedVariant &&
      matchedVariant.price
        ? matchedVariant.price
        : 0
    ) * 100
  ),

comparePrice:
  Math.round(
    parseFloat(
      matchedVariant &&
      matchedVariant.compare_at_price
        ? matchedVariant.compare_at_price
        : (
            matchedVariant &&
            matchedVariant.price
              ? matchedVariant.price
              : 0
          )
    ) * 100
  )
      };


      variantCache[id] =
        data;

      return data;
    })

    .catch(function (error) {
      console.warn(
        '[locked-gifts] product lookup failed:',
        id,
        error
      );

      var fallback = {
        id: id,
        title: 'Free Gift',
        image: '',
        price: 0,
        comparePrice: 0
      };

      variantCache[id] =
        fallback;

      return fallback;
    });
}


  /* ============================================================
     MONEY
  ============================================================ */

  function money(
    cents
  ) {

    cents =
      Number(cents || 0);


    if (!cents) {
      return '';
    }


    return (
      '₹' +
      Math.round(
        cents / 100
      )
    );

  }


  /* ============================================================
     LOCK ICON
  ============================================================ */

  function lockIcon() {

    return (

      '<svg ' +
        'width="14" ' +
        'height="14" ' +
        'viewBox="0 0 24 24" ' +
        'fill="none" ' +
        'aria-hidden="true">' +

        '<rect ' +
          'x="5" ' +
          'y="10" ' +
          'width="14" ' +
          'height="11" ' +
          'rx="2" ' +
          'stroke="currentColor" ' +
          'stroke-width="2">' +
        '</rect>' +

        '<path ' +
          'd="M8 10V7a4 4 0 0 1 8 0v3" ' +
          'stroke="currentColor" ' +
          'stroke-width="2" ' +
          'stroke-linecap="round">' +
        '</path>' +

      '</svg>'

    );

  }


  /* ============================================================
     CREATE LOCKED ROW

     Important:
     It uses your SAME existing cart-freebies classes.

     Therefore the styling stays consistent with your
     current Free Gifts section.

     It deliberately DOES NOT have:
       cart-line--gift
       data-cart-item

     So your cart engine never treats the placeholder
     like a real Shopify cart line.
  ============================================================ */

  function createLockedGiftRow(
    product
  ) {

    var row =
      document.createElement(
        'div'
      );


    row.className =
      'cart-freebies__item cart-freebies__item--locked';


    row.setAttribute(
      'data-locked-gift',
      product.id
    );


    row.setAttribute(
      'data-variant-id',
      product.id
    );


    /* -------------------------------
       IMAGE
    -------------------------------- */

    var imageHTML = '';


    if (product.image) {

      imageHTML =

        '<img ' +

          'class="cart-freebies__img" ' +

          'src="' +
            product.image +
          '" ' +

          'alt="" ' +

          'loading="lazy">' ;

    }


    /* -------------------------------
       MRP
    -------------------------------- */

    var mrp =

      product.comparePrice > 0
        ? product.comparePrice
        : product.price;


    var mrpHTML = '';


    if (mrp > 0) {

      mrpHTML =

        '<span class="cart-freebies__mrp">' +

          money(mrp) +

        '</span>';

    }


    /* -------------------------------
       FINAL HTML
    -------------------------------- */

    row.innerHTML =

      '<span class="cart-freebies__media">' +

        imageHTML +

        '<span class="cart-freebies__free">' +
          '₹1' +
        '</span>' +

      '</span>' +


      '<div class="cart-freebies__body">' +

        '<span class="cart-freebies__name">' +
          product.title +
        '</span>' +

        '<span class="cart-freebies__price">' +

          mrpHTML +

          '<span class="cart-freebies__now">' +
            '₹1' +
          '</span>' +

        '</span>' +

      '</div>' +


      '<span class="cart-freebies__lock" data-locked-gift-icon>' +

        lockIcon() +

      '</span>';


    return row;

  }


  /* ============================================================
     REMOVE LOCKED PLACEHOLDER WHEN REAL GIFT EXISTS
  ============================================================ */

  function removeUnlockedPlaceholders() {

    var list =
      getGiftList();


    if (!list) {
      return;
    }


    list
      .querySelectorAll(
        '[data-locked-gift]'
      )
      .forEach(
        function (lockedRow) {

          var id =
            lockedRow.getAttribute(
              'data-locked-gift'
            );


          if (
            hasRealGift(id)
          ) {

            lockedRow.remove();

          }

        }
      );

  }


  /* ============================================================
     RENDER MISSING LOCKED GIFTS
  ============================================================ */

  function renderLockedGifts() {

    try {

      var list =
        getGiftList();


      if (!list) {
        return;
      }


      /*
        First remove placeholders that have
        now become real gifts.
      */

      removeUnlockedPlaceholders();


      var giftIds =
        getConfiguredGiftIds();


      giftIds.forEach(
        function (variantId) {

          /*
            Already unlocked?
            Do nothing.
          */

          if (
            hasRealGift(variantId)
          ) {
            return;
          }


          /*
            Locked placeholder already present?
            Do nothing.
          */

          if (
            list.querySelector(
              '[data-locked-gift="' +
              variantId +
              '"]'
            )
          ) {
            return;
          }


          /*
            Load product details and add it
            inside THIS SAME gift list.
          */

          loadVariant(
            variantId
          )
            .then(
              function (product) {

                /*
                  Drawer may have re-rendered
                  while request was running.
                */

                var freshList =
                  getGiftList();


                if (!freshList) {
                  return;
                }


                /*
                  It may have unlocked while
                  product data was loading.
                */

                if (
                  hasRealGift(
                    variantId
                  )
                ) {
                  return;
                }


                if (
                  freshList.querySelector(
                    '[data-locked-gift="' +
                    variantId +
                    '"]'
                  )
                ) {
                  return;
                }


                freshList.appendChild(
                  createLockedGiftRow(
                    product
                  )
                );


                updateUnlockedBadge();

              }
            );

        }
      );


      updateUnlockedBadge();

    } catch (error) {

      /*
        A display issue here must never
        interfere with the cart.
      */

      console.warn(
        '[locked-gifts] render skipped',
        error
      );

    }

  }


  /* ============================================================
     STYLES

     Only locked-state styles.
     Your normal gift styles remain untouched.
  ============================================================ */

  function addStyles() {

    if (
      document.getElementById(
        STYLE_ID
      )
    ) {
      return;
    }


    var style =
      document.createElement(
        'style'
      );


    style.id =
      STYLE_ID;


    style.textContent = `

      /*
        Locked placeholder uses exactly
        the existing cart-freebies row.
      */

      .cart-freebies__item--locked {
        position: relative;
        opacity: .52;
        pointer-events: none;
      }


      .cart-freebies__item--locked
      .cart-freebies__img {
        filter: grayscale(100%);
        opacity: .55;
      }


      .cart-freebies__item--locked
      .cart-freebies__free {
        opacity: .7;
      }


      .cart-freebies__item--locked
      .cart-freebies__name,
      .cart-freebies__item--locked
      .cart-freebies__price {
        opacity: .7;
      }


      .cart-freebies__lock {
        position: absolute;

        right: 12px;

        top: 50%;

        transform:
          translateY(-50%);

        display: flex;

        align-items: center;

        justify-content: center;

        width: 30px;

        height: 30px;

        border-radius: 50%;

        background: #fff;

        border:
          1px solid rgba(0,0,0,.12);

        box-shadow:
          0 2px 7px rgba(0,0,0,.08);

        color: #222;

        opacity: 1 !important;

        z-index: 5;
      }

    `;


    document.head.appendChild(
      style
    );

  }


  /* ============================================================
     SAFE RE-RENDER SCHEDULER
  ============================================================ */

  function scheduleRender() {

    clearTimeout(
      renderTimer
    );


    renderTimer =
      setTimeout(
        function () {

          renderLockedGifts();

        },
        150
      );

  }


  /* ============================================================
     WATCH DRAWER HTML

     The existing cart engine replaces cart drawer
     HTML after changes.

     We only re-run display code afterwards.

     We DO NOT trigger cart events.
  ============================================================ */

  function watchDrawer() {

    var drawer =
      document.querySelector(
        '[data-drawer="drawer-cart"]'
      );


    if (!drawer) {

      setTimeout(
        watchDrawer,
        500
      );

      return;

    }


    if (
      drawerObserver
    ) {

      drawerObserver.disconnect();

    }


    drawerObserver =
      new MutationObserver(
        function (mutations) {

          var relevant =
            false;


          for (
            var i = 0;
            i < mutations.length;
            i++
          ) {

            var target =
              mutations[i].target;


            /*
              Ignore our own locked rows.
              Prevents observer loops.
            */

            if (
              target &&
              target.nodeType === 1 &&
              target.closest &&
              target.closest(
                '[data-locked-gift]'
              )
            ) {

              continue;

            }


            relevant =
              true;

            break;

          }


          if (relevant) {

            scheduleRender();

          }

        }
      );


    drawerObserver.observe(
      drawer,
      {
        childList: true,
        subtree: true
      }
    );


    renderLockedGifts();

  }


  /* ============================================================
     INIT
  ============================================================ */

  function initLockedGifts() {

    config =
      readOfferConfig();


    if (!config) {

      console.warn(
        '[locked-gifts] cart offer config missing'
      );

      return;

    }


    addStyles();


    setTimeout(
      watchDrawer,
      300
    );


    setTimeout(
      renderLockedGifts,
      800
    );

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initLockedGifts,
      {
        once: true
      }
    );

  } else {

    initLockedGifts();

  }

})();